import { useNavigate } from 'react-router';
import { api } from '../api.js';
import { useAction } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { ConfirmAction } from './ConfirmAction.js';
import { fmtDateTime } from './format.js';
import { ErrorNotice, Notice } from './Notice.js';
import { StatusPill } from './StatusPill.js';

export function OverviewTab() {
  const { app, setApp, reload } = useApp();
  const navigate = useNavigate();

  const stop = useAction(async () => {
    const { app: next } = await api.stopApp(app.slug);
    setApp(next);
  });
  const start = useAction(async () => {
    const { app: next } = await api.startApp(app.slug);
    setApp(next);
  });
  const remove = useAction(async () => {
    await api.deleteApp(app.slug);
    navigate('/', { replace: true });
  });

  const url = `https://${app.host}`;

  return (
    <>
      {app.status === 'provisioning' ? (
        <Notice kind="info">
          <strong>Setting up your address.</strong> This usually takes 5–10 minutes. You can upload your first
          version from the Deploy tab in the meantime.
        </Notice>
      ) : null}
      {app.status === 'failed' ? (
        <Notice kind="warn">
          <strong>Setting up this app didn't work.</strong>{' '}
          {app.statusMessage ?? 'No further detail was recorded.'} You can delete it and try again.
        </Notice>
      ) : null}
      {app.status === 'stopped' ? (
        <Notice kind="info">
          <strong>This app is stopped.</strong> Anyone who opens it sees a "not available" page until you
          start it again.
        </Notice>
      ) : null}
      {app.status === 'deleting' ? (
        <Notice kind="warn">This app is being deleted. It will disappear from your list shortly.</Notice>
      ) : null}

      <section className="card">
        <h2>Details</h2>
        <dl className="kv">
          <dt>Status</dt>
          <dd>
            <StatusPill status={app.status} />
            {app.statusMessage && app.status !== 'failed' ? (
              <span className="small subtle"> — {app.statusMessage}</span>
            ) : null}
          </dd>
          <dt>Address</dt>
          <dd>
            {app.status === 'active' ? (
              <a href={url} target="_blank" rel="noreferrer">
                {url}
              </a>
            ) : (
              <span className="mono">{url}</span>
            )}
          </dd>
          <dt>Kind</dt>
          <dd>{app.type === 'static' ? 'Static site' : 'Server app (AWS Lambda)'}</dd>
          <dt>Current version</dt>
          <dd>
            {app.currentDeployId ? (
              <span className="mono">{app.currentDeployId}</span>
            ) : (
              <span className="subtle">Nothing deployed yet</span>
            )}
          </dd>
          <dt>Created</dt>
          <dd>{fmtDateTime(app.createdAt)}</dd>
        </dl>
        <p className="small subtle">
          Only people you add under People can open the address. They sign in with Xero; nobody else gets past
          the front door.
        </p>
      </section>

      <section className="card">
        <h2>Actions</h2>
        <ErrorNotice error={stop.error ?? start.error ?? remove.error} />
        <div className="btn-row">
          {app.status === 'active' ? (
            <ConfirmAction
              label="Stop app"
              message="Stopping makes the app unavailable to everyone straight away. Your files and settings are kept, and you can start it again any time."
              confirmLabel="Stop app"
              pending={stop.pending}
              onConfirm={() => stop.run()}
            />
          ) : null}
          {app.status === 'stopped' ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={start.pending}
              onClick={() => void start.run()}
            >
              {start.pending ? 'Starting…' : 'Start app'}
            </button>
          ) : null}
          <ConfirmAction
            label="Delete app"
            message={`Delete "${app.name}"? This removes the app, its address and everything uploaded to it. People you invited lose access immediately. This can't be undone.`}
            confirmLabel="Delete permanently"
            danger
            disabled={app.status === 'deleting' || app.status === 'provisioning'}
            pending={remove.pending}
            onConfirm={() => remove.run()}
          />
          <button type="button" className="btn btn-link" onClick={reload}>
            Refresh
          </button>
        </div>
        {app.status === 'provisioning' ? (
          <p className="hint" style={{ marginTop: '0.75rem' }}>
            Delete becomes available once setup has finished.
          </p>
        ) : null}
      </section>
    </>
  );
}
